/**
 * A single dropdown block option, normalized from the block's stored JSON.
 */
export type DropdownOption = {
  label: string;
  value: string;
  className?: string;
};

/**
 * Normalize a token/phrase label that may arrive as a plain string or as an
 * object shape from the notes domain. Returns '' for anything unrecognized.
 *
 * External shapes handled, e.g.:
 *   'OK'
 *   { default_text: 'OK' } | { defaultText: 'OK' } | { i18n_key: 'battery.ok' }
 */
export const normalizeLabel = (label: unknown): string => {
  if (typeof label === 'string') {
    return label;
  }
  if (label && typeof label === 'object') {
    const { default_text, defaultText, i18n_key } = label as {
      default_text?: string;
      defaultText?: string;
      i18n_key?: string;
    };
    return default_text || defaultText || i18n_key || '';
  }
  return '';
};

/**
 * Parse the dropdown block's `options` prop (stored as a JSON string) into a
 * normalized option list. Returns [] on missing/invalid JSON.
 *
 * External shape (stored on the block prop), e.g.:
 *   '[{"label":{"default_text":"OK"},"value":"ok","className":"bg-green-50 ..."}]'
 *
 * Note: empty-`value` options are intentionally kept (they render as blank
 * choices); this differs from `parseDropdownTokenOptions` in the inline-token
 * area, which filters them out.
 */
export function parseDropdownBlockOptions(
  optionsString: string | undefined,
): DropdownOption[] {
  if (!optionsString) {
    return [];
  }
  try {
    const parsed = JSON.parse(optionsString || '[]') as Array<{
      label?: string | { default_text?: string; defaultText?: string };
      value?: string;
      className?: string;
    }>;
    return parsed.map((opt) => ({
      label:
        normalizeLabel(opt.label) ||
        (opt.value !== undefined ? String(opt.value) : ''),
      value: opt.value ?? '',
      className: opt.className,
    }));
  } catch {
    return [];
  }
}
